import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Form,
  Input,
  Select,
  Tag,
  Typography,
  Modal,
  Popconfirm,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  assignSubscriptionUser,
  fetchSubscriptions,
  updateSubscriptionAccess,
} from "../../../api/subscriptionsApi";
import { fetchFirms } from "../../../api/firmApi";
import { fetchAllLawyers } from "../../../api/lawyerApi";
import { toastError, toastSuccess } from "../../../utils/toast";

const { Option } = Select;

const STATUS_COLORS = {
  unpaid: "default",
  pending: "processing",
  paid: "success",
  failed: "error",
  cancelled: "warning",
};

const ACCESS_COLORS = {
  active: "success",
  expired: "warning",
  suspended: "error",
  none: "default",
};

export default function SubscriptionsPage() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [users, setUsers] = useState([]);
  const [assigning, setAssigning] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchForm] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const [data, firms, lawyers] = await Promise.all([
        fetchSubscriptions(),
        fetchFirms().catch(() => []),
        fetchAllLawyers().catch(() => []),
      ]);
      const list = Array.isArray(data) ? data : [];
      setRows(list);
      setFiltered(list);
      const options = [
        ...(Array.isArray(firms) ? firms : []).map((item) => ({
          id: item.user_id || item.id,
          name: item.user_name || item.name,
          email: item.email,
          role: "firm",
        })),
        ...(Array.isArray(lawyers) ? lawyers : []).map((item) => ({
          id: item.user_id || item.id,
          name: item.user_name || item.name,
          email: item.email,
          role: "lawyer",
        })),
      ].filter((item) => item.id);
      setUsers(options);
    } catch (e) {
      toastError("Failed to load subscriptions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const applyFilters = (values) => {
    const q = (values.search || "").trim().toLowerCase();
    let next = [...rows];

    if (q) {
      next = next.filter((row) => {
        const name = `${row.first_name || ""} ${row.last_name || ""}`.toLowerCase();
        const user = `${row.user_name || ""} ${row.user_email || ""}`.toLowerCase();
        return (
          name.includes(q) ||
          user.includes(q) ||
          (row.email || "").toLowerCase().includes(q) ||
          (row.phone || "").toLowerCase().includes(q) ||
          (row.package_name || "").toLowerCase().includes(q) ||
          (row.payment_reference || "").toLowerCase().includes(q)
        );
      });
    }

    if (values.payment_status) {
      next = next.filter((row) => row.payment_status === values.payment_status);
    }
    if (values.access_state) {
      next = next.filter((row) => row.access_state === values.access_state);
    }
    if (values.subscription_type) {
      next = next.filter((row) => row.subscription_type === values.subscription_type);
    }
    if (values.billing_cycle) {
      next = next.filter((row) => row.billing_cycle === values.billing_cycle);
    }

    setFiltered(next);
  };

  const onReset = () => {
    searchForm.resetFields();
    setFiltered(rows);
  };

  const onAssign = async () => {
    if (!assigning) return;
    setSaving(true);
    try {
      await assignSubscriptionUser(assigning.id, selectedUserId || 0);
      toastSuccess(selectedUserId ? "User linked to this subscription." : "User unassigned.");
      setAssigning(null);
      setSelectedUserId(null);
      await load();
    } catch (err) {
      toastError(err?.response?.data?.message || "Failed to assign user.");
    } finally {
      setSaving(false);
    }
  };

  const onAccess = async (row, accessStatus) => {
    try {
      await updateSubscriptionAccess(row.id, accessStatus);
      toastSuccess(accessStatus === "suspended" ? "Subscription suspended." : "Subscription restored.");
      await load();
    } catch (err) {
      toastError(err?.response?.data?.message || "Failed to update access.");
    }
  };

  const columns = [
    {
      title: "Customer",
      key: "customer",
      render: (_, row) => (
        <div>
          <strong>
            {row.first_name} {row.last_name}
          </strong>
          <div>{row.email}</div>
          <div>{row.phone}</div>
        </div>
      ),
    },
    {
      title: "Linked user",
      key: "user",
      render: (_, row) =>
        row.user_id ? (
          <div>
            <strong>{row.user_name || "User"}</strong>
            <div style={{ textTransform: "capitalize" }}>{row.user_role}</div>
            <div>{row.user_email}</div>
          </div>
        ) : (
          <span style={{ color: "#94a3b8" }}>Not linked</span>
        ),
    },
    {
      title: "Package",
      dataIndex: "package_name",
      render: (text, row) => (
        <div>
          <strong>{text}</strong>
          <div style={{ textTransform: "capitalize" }}>
            {row.subscription_type} / {row.billing_cycle}
          </div>
        </div>
      ),
    },
    {
      title: "Price",
      dataIndex: "price",
      render: (price, row) => `${row.currency || "AED"} ${Number(price || 0).toLocaleString()}`,
    },
    {
      title: "Payment",
      dataIndex: "payment_status",
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || "default"}>
          {(status || "unpaid").toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Access",
      dataIndex: "access_state",
      render: (state) => (
        <Tag color={ACCESS_COLORS[state] || "default"}>
          {(state || "none").toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Ends",
      dataIndex: "ends_at",
      render: (d) => (d ? dayjs(d).format("YYYY-MM-DD") : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, row) => (
        <Space wrap>
          <Button
            size="small"
            onClick={() => {
              setAssigning(row);
              setSelectedUserId(row.user_id || null);
            }}
          >
            {row.user_id ? "Change user" : "Link user"}
          </Button>
          {row.payment_status === "paid" && row.access_state !== "suspended" ? (
            <Popconfirm
              title="Suspend this subscription?"
              onConfirm={() => onAccess(row, "suspended")}
            >
              <Button size="small" danger>
                Suspend
              </Button>
            </Popconfirm>
          ) : null}
          {row.access_status === "suspended" || row.access_state === "suspended" ? (
            <Button size="small" type="primary" onClick={() => onAccess(row, "active")}>
              Restore
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <Card bordered={false} style={{ borderRadius: 12 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "center",
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <Typography.Title level={4} style={{ margin: 0 }}>
          Subscriptions
        </Typography.Title>
        <Form form={searchForm} layout="inline" onFinish={applyFilters}>
          <Form.Item name="search">
            <Input
              allowClear
              placeholder="Search name, email, package"
              prefix={<SearchOutlined />}
              style={{ minWidth: 220 }}
            />
          </Form.Item>
          <Form.Item name="payment_status">
            <Select placeholder="Payment" allowClear style={{ minWidth: 130 }}>
              <Option value="unpaid">Unpaid</Option>
              <Option value="pending">Pending</Option>
              <Option value="paid">Paid</Option>
              <Option value="failed">Failed</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
          </Form.Item>
          <Form.Item name="access_state">
            <Select placeholder="Access" allowClear style={{ minWidth: 130 }}>
              <Option value="active">Active</Option>
              <Option value="expired">Expired</Option>
              <Option value="suspended">Suspended</Option>
              <Option value="none">None</Option>
            </Select>
          </Form.Item>
          <Form.Item name="subscription_type">
            <Select placeholder="Type" allowClear style={{ minWidth: 120 }}>
              <Option value="personal">Personal</Option>
              <Option value="business">Business</Option>
            </Select>
          </Form.Item>
          <Form.Item name="billing_cycle">
            <Select placeholder="Cycle" allowClear style={{ minWidth: 120 }}>
              <Option value="monthly">Monthly</Option>
              <Option value="annual">Annual</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                Search
              </Button>
              <Button onClick={onReset}>Reset</Button>
            </Space>
          </Form.Item>
        </Form>
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={filtered}
        loading={loading}
        scroll={{ x: 1100 }}
        pagination={{ pageSize: 10, showSizeChanger: true }}
        expandable={{
          expandedRowRender: (record) => (
            <div>
              <p>
                <strong>Duration:</strong> {record.duration_value} {record.duration_unit}
              </p>
              <p>
                <strong>Starts:</strong> {record.starts_at ? dayjs(record.starts_at).format("YYYY-MM-DD HH:mm") : "-"}
                {" · "}
                <strong>Ends:</strong> {record.ends_at ? dayjs(record.ends_at).format("YYYY-MM-DD HH:mm") : "-"}
              </p>
              <p>
                <strong>Description:</strong> {record.description || "-"}
              </p>
              <p>
                <strong>Features at purchase:</strong>
              </p>
              <ul>
                {(record.features || []).map((feature, idx) => (
                  <li key={idx}>{typeof feature === "string" ? feature : feature.feature_text}</li>
                ))}
              </ul>
            </div>
          ),
        }}
      />

      <Modal
        title="Link a firm or lawyer"
        open={!!assigning}
        onCancel={() => {
          setAssigning(null);
          setSelectedUserId(null);
        }}
        onOk={onAssign}
        confirmLoading={saving}
        okText="Save"
      >
        <p style={{ marginBottom: 12 }}>
          {assigning?.package_name} · {assigning?.email}
        </p>
        <Select
          showSearch
          allowClear
          placeholder="Select a firm or lawyer user"
          style={{ width: "100%" }}
          value={selectedUserId || undefined}
          onChange={(value) => setSelectedUserId(value || null)}
          optionFilterProp="label"
          options={users.map((user) => ({
            value: user.id,
            label: `${user.name} (${user.role}) — ${user.email}`,
          }))}
        />
      </Modal>
    </Card>
  );
}
